const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:4010';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : {};

  if (!response.ok) {
    const err = (body as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(
      response.status,
      err?.code ?? 'unknown',
      err?.message ?? 'Something went wrong.',
    );
  }
  return body as T;
}

export interface PaymentView {
  payment: {
    id: string;
    amount: number;
    currency: string;
    amount_display: string;
    description: string | null;
    merchant_reference: string | null;
    status: string;
    expires_at: string | null;
    /**
     * When the current 15-minute payment session runs out, ISO, or null once
     * the payment is no longer payable. The page counts down to this, not to
     * `expires_at` (which is how long the link stays openable at all).
     */
    session_expires_at: string | null;
  };
  merchant: {
    name: string;
    country: string | null;
    /**
     * The merchant's own logo, inline, or null when they have not uploaded one.
     *
     * A `data:` URI rather than a URL for exactly the reason the advertising
     * banner is one: `img-src` on this page is `'self' data: https://*.stripe.com`
     * and this API is a different origin, so an <img> pointing at it would be
     * blocked - and the policy doing the blocking is what keeps the page inside
     * PCI DSS SAQ A, so it is not something to widen for a logo. The bytes
     * arrive over a call the policy already permits and render through a scheme
     * it already permits.
     */
    logo_data_uri: string | null;
  };
  stripe: { configured: boolean; publishable_key: string | null };
  /**
   * The two facts the security note in the footer is allowed to state.
   *
   * Both are nullable and both are configured in the admin console rather than
   * written here, because both go out of date - the standard when the Council
   * publishes a new version, the scan date every quarter. Null means the
   * platform has not published that fact and the corresponding line is not
   * drawn at all. The component never substitutes a date of its own, which is
   * the whole reason these arrive from the server instead of being constants in
   * a component somebody has to remember to edit.
   */
  assurance: {
    standard: string | null;
    last_scan_date: string | null;
  };
}

export interface IntentView {
  client_secret: string;
  publishable_key: string;
  /** Null when the charge is on the platform account (a platform-owned merchant). */
  connected_account: string | null;
  attempt_id: string;
}

export interface StatusView {
  status: string;
  confirmed: boolean;
  failed: boolean;
  pending: boolean;
  amount: number;
  currency: string;
  amount_display: string;
  paid_at: string | null;
  payment_method_type: string | null;
  card: { brand: string | null; last4: string | null } | null;
  failure: { code: string | null; decline_code: string | null; message: string | null } | null;
}

export interface MethodsView {
  source: string;
  unavailable: Array<{ method: string; reason: string; detail: string }>;
}

/**
 * The advertising slot.
 *
 * `image_data_uri` is the image itself, inline. The payment page's CSP allows
 * images from `'self'` and from `data:`, and this API is a different origin, so
 * an <img> pointing at it would be blocked - and the policy that blocks it is
 * the same one that keeps the page inside PCI DSS SAQ A, so it is not something
 * to widen for an advertisement. The bytes therefore arrive over a call the
 * policy already permits and render through a scheme it already permits.
 */
export interface BannerView {
  /**
   * One banner, or none. Never a list.
   *
   * Banners rotate, they do not stack: the server picks exactly one for this
   * page view, and the shape of this type is what makes "two advertisements on
   * one payment page" something nobody can do by accident.
   */
  banner: {
    id: string;
    alt_text: string;
    destination_url: string;
    /**
     * The named format, and the box it is drawn in, in CSS pixels.
     *
     * Every creative in a slot has the same dimensions, so the page can reserve
     * the space before the bytes arrive and the slot no longer changes shape
     * between one advertisement and the next. Null on a creative uploaded
     * before the formats were defined - those are bounded instead.
     */
    slot: string | null;
    display_width: number | null;
    display_height: number | null;
    image_data_uri: string;
  } | null;
}

export interface SubscriptionChargeView {
  id: string;
  state: string;
  amount: number;
  currency: string;
  kind: string;
  merchantName: string;
  tier: string | null;
  expiresAt: string;
}

export const api = {
  load: (token: string) => request<PaymentView>(`/pay/${token}`),
  intent: (token: string) => request<IntentView>(`/pay/${token}/intent`, { method: 'POST' }),
  status: (token: string) => request<StatusView>(`/pay/${token}/status`),
  methods: (token: string) => request<MethodsView>(`/pay/${token}/methods`),

  /**
   * Close a paid link so its URL stops working. Called by the success screen a
   * few seconds before it closes itself. Fire and forget with keepalive, so it
   * still reaches the server if the tab is closing.
   */
  close: (token: string) =>
    request<{ ok: boolean }>(`/pay/${token}/close`, { method: 'POST', keepalive: true }).catch(
      () => ({ ok: false }),
    ),

  // A merchant paying the platform for a fee-waiver package. The intent is on
  // the platform account, so no connected-account id comes back with it.
  subCharge: (token: string) => request<SubscriptionChargeView>(`/pay/sub/${token}`),
  subIntent: (token: string) =>
    request<{ clientSecret: string; publishableKey: string; paymentMethodTypes: string[] }>(`/pay/sub/${token}/intent`, { method: 'POST' }),
  subStatus: (token: string) => request<{ state: string }>(`/pay/sub/${token}/status`),
  banner: (token: string) => request<BannerView>(`/pay/${token}/banner`),

  /**
   * Count a display or a click. Fire and forget.
   *
   * `keepalive` so the count survives the browser turning its attention to the
   * new tab a click opens. No credentials are attached - fetch defaults to
   * same-origin credentials and this is a cross-origin call, so no cookie is
   * sent, and the server sets none.
   */
  bannerEvent: (token: string, id: string, event: 'impression' | 'click') =>
    request<Record<string, never>>(`/pay/${token}/banner/${id}/${event}`, {
      method: 'POST',
      keepalive: true,
    }),
};
